/*
 * © Copyright 2014 – 2023 Open Text or one of its affiliates.
 *
 * The only warranties for products and services of Open Text and its affiliates and licensors ("Open Text") are as
 * may be set forth in the express warranty statements accompanying such products and services. Nothing herein should be
 * constituting an additional warranty. Open Text shall not be liable for technical or editorial errors or omissions
 * contained herein.
 *
 * The information contained herein is subject to change without notice.
 */

import React from 'react';
import PropTypes from 'prop-types';
import * as ReactDOM from 'react-dom';
import t from '../i18n/locale-keys';

/* Displays a dialog that stays open when this.props.open is true.
 * - If an onCancel event handler is provided, the "Cancel" button will be shown; clicking "X" or Cancel will call it.
 * - If no onCancel event handler is provided, clicking "X" will call the onClose event handler instead.
 * - Clicking "OK" always calls the onClose event handler.
 */
export default class Dialog extends React.PureComponent {
    handleBackdropClick = (event) => {
        if (this.props.exitOnBackdropClick && event.target.className === 'ias-dialog') {
            this.props.onClose();
        }
    };

    handleCancel = (event) => {
        if (this.props.onCancel) {
            this.props.onCancel(event);
        }
        else {
            this.props.onClose(event);
        }
    };

    render() {
        if (!this.props.open) {
            return null;
        }

        // eslint-disable-next-line max-len
        const {addContainerClass, omitActionButtons, omitCloseButton, onCancel, onClose, title, actionButtonsWithDelete} = this.props;

        let dialogCloseButton = null;
        if (!omitCloseButton) {
            dialogCloseButton = (
                <button
                    className="ias-button ias-icon-button ias-dialog-cancel-button"
                    id="Dialog_Close_Button"
                    onClick={this.handleCancel}
                    title={t.buttonClose()}
                >
                    <i className="ias-icon ias-icon-close_thick" />
                </button>
            );
        }

        let actionButtons = null;
        let cancelButton = null;
        if (!omitActionButtons) {
            if (onCancel) {
                cancelButton = (
                    <button className="ias-button" id="Dialog_Cancel_Button" onClick={this.handleCancel}>
                        {t.buttonCancel()}
                    </button>
                );
            }
            const actionButtonText = actionButtonsWithDelete ? t.buttonDelete() : t.buttonOk();
            const actionButtonClass = actionButtonsWithDelete ? 'ias-button btn-danger' : 'ias-button';

            actionButtons = (
                <div className="ias-actions">
                    <button className={actionButtonClass} id="Dialog_OK_Button" onClick={onClose}>
                        {actionButtonText}
                    </button>
                    {cancelButton}
                </div>
            );
        }

        let containerClass = 'ias-dialog-container';
        if (addContainerClass) {
            containerClass += ' ' + addContainerClass;
        }

        const dialog = (
            <div className="ias-dialog" onClick={this.handleBackdropClick}>
                <div className={containerClass}>
                    <div className="ias-dialog-content">
                        {title}
                        {this.props.children}
                        {dialogCloseButton}
                        {actionButtons}
                    </div>
                </div>
            </div>
        );

        return ReactDOM.createPortal(dialog, document.body);
    }
}

Dialog.propTypes = {
    addContainerClass: PropTypes.string,
    exitOnBackdropClick: PropTypes.bool,
    onCancel: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    title: PropTypes.node
};
